import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { IPagination2, ISorting } from "../../../interfaces/Components/ITable";
import { useTablePreferences } from "../../../hooks/useTablePreferences";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import "../../../assets/css/table/Table.css";
import { CSS } from "@dnd-kit/utilities";
import SortAsending from "../../Atomos/Icons/SortAsending";
import SortDecending from "../../Atomos/Icons/SortDecending";
import SortDefault from "../../Atomos/Icons/SortDefault";
import PaginationComponent from "../../Atomos/Pagination/PaginationComponent";
import ExcelModal from "../../Atomos/Modals/ExcelModal";
import LegacyButton from "../../Atomos/Buttons/LegacyButton";
import Excel from "../../Atomos/Icons/Excel";
import ChevronDown from "../../Atomos/Icons/ChevronDown";
import WSGCheckbox from "../../Atomos/Checkbox/WSGCheckBox";
import Vector from "../../Atomos/Icons/Vector";
import {
  exportStyledExcel,
  getExcelTitleFromFilename,
} from "../../../utils/excel/exportStyledExcel";
import "../../../assets/css/table/Pagination.css";

interface IHeader {
  id: string;
  header: string | React.ReactNode;
  width?: number;
  enableSorting?: boolean;
  cell?: (props: { row: any; rowIndex: number; value: any }) => React.ReactNode;
}

interface IOtherButton {
  text: string;
  className?: string;
  onClick: () => void;
}

interface ITableProps {
  data: any[];
  totalCount: number;
  columnsDef: IHeader[];
  sorting: ISorting;
  pagination: IPagination2;
  endpoint?: string;
  onSortingChange: (sorting: ISorting) => void;
  onPaginationChange: (pagination: IPagination2) => void;
  showColumnVisibility?: boolean;
  showExcelExport?: boolean;
  otherButtons?: IOtherButton[];
  bottomPagination?: boolean;
  topPagination?: boolean;
  excelHeaders?: string[];
  formatExcelData?: (data: any[], isCurrentData?: boolean) => any[] | Promise<any[]>;
  formatData?: (data: any[]) => any[];
  emptyStateMessage?: string | React.ReactNode;
  onExportAll?: () => Promise<any[]>;
  excelFilename?: string;
  tableKey?: string | null;
  forcedColumnVisibility?: Record<string, boolean>;
  defaultColumnVisibility?: Record<string, boolean>;
  topContent?: React.ReactNode;
  getRowClassName?: (row: any, rowIndex: number) => string;
}

const FIELDS_TO_IGNORE = new Set(["id", "icon", "count", "edit", "actions", "subscribeCheck"]);

function safeCellValue(value: any): React.ReactNode {
  if (value === null || value === undefined) {
    return "";
  }
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

function safeExcelValue(value: any): string | number | boolean {
  if (value === null || value === undefined) {
    return "";
  }
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return value;
}

function ColumnVisibilityControlSimple({
  headers,
  columnVisibility,
  onToggle,
}: {
  headers: IHeader[];
  columnVisibility: Record<string, boolean>;
  onToggle: (columnId: string) => void;
}) {
  const [dropdown, setDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dropdown) {
      return;
    }
    const onDocMouseDown = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdown(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [dropdown]);

  const hiddenByDefault = new Set(["index", "actions"]);
  const visibleHeaders = headers.filter((h) => !hiddenByDefault.has(h.id));

  return (
    <div className="w-full relative flex text-left h-full items-center" ref={dropdownRef}>
      <div className="flex w-full items-center justify-center h-full">
        <LegacyButton
          text="Show / Hide Columns"
          onClick={() => setDropdown((v) => !v)}
          icon={
            <div className="w-5 h-5 ml-2 flex items-center justify-center">
              <ChevronDown
                rotation={dropdown ? "rotate(180deg)" : "rotate(0deg)"}
              />
            </div>
          }
          content="textIcon"
          color="primary"
          style="filled"
          className="w-full"
        />
      </div>

      <div
        className={`absolute top-12 right-0 w-max ${
          dropdown
            ? "opacity-1 visible scale-[100%] duration-0"
            : "opacity-0 invisible scale-95 duration-200"
        } transform ease-out transition-opacity duration-200 transition-delay-200 z-10 origin-top-right bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
        role="menu"
        aria-orientation="vertical"
        aria-labelledby="menu-button"
        tabIndex={-1}
      >
        {visibleHeaders.map((h) => {
          const checked = columnVisibility[h.id] !== false;
          const headerTitle = getHeaderTitle(h.header, h.id);
          return (
            <label key={h.id}>
              <WSGCheckbox
                checked={checked}
                index={0}
                text={headerTitle}
                handleCheckboxChange={() => onToggle(h.id)}
                className="px-4"
              />
              <Vector className="w-full h-0 stroke-gray-50 stroke-1" />
            </label>
          );
        })}
      </div>
    </div>
  );
}

function buildExcelFilename(base?: string) {
  const currentDate = new Date();
  const formattedDate = `${String(currentDate.getMonth() + 1).padStart(
    2,
    "0"
  )}_${String(currentDate.getDate()).padStart(
    2,
    "0"
  )}_${currentDate.getFullYear()}`;
  const safeBase = (base || "export")
    .replace(/^\/+/, "")
    .replace(/\?.*$/, "")
    .replace(/[^\w-]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return `${safeBase || "export"}_${formattedDate}.xlsx`;
}

function nextSort(current: ISorting, field: string): ISorting {
  if (current.field !== field) {
    return { field, order: "asc" };
  }
  return { field, order: current.order === "asc" ? "desc" : "asc" };
}

function ColumnResizeHandle({
  onResize,
  columnId,
  minWidth,
}: {
  onResize: (columnId: string, newWidth: number) => void;
  columnId: string;
  minWidth?: number;
}) {
  const handleRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!handleRef.current) {
        return;
      }
      const th = handleRef.current.closest("th");
      if (!th) {
        return;
      }

      const rect = th.getBoundingClientRect();
      const minAllowedWidth = minWidth || 50;
      const newWidth = Math.max(minAllowedWidth, e.clientX - rect.left);
      onResize(columnId, newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, columnId, onResize, minWidth]);

  return (
    <div
      ref={handleRef}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
      }}
      className="absolute top-0 right-0 w-2 h-full cursor-col-resize hover:bg-blue-400 opacity-0 hover:opacity-100 transition-opacity z-20"
      style={{ touchAction: "none", marginRight: "-4px" }}
      aria-label="Resize column"
      role="separator"
      aria-orientation="vertical"
    />
  );
}

function DraggableHeaderCell({
  header,
  sorting,
  onSortingClick,
  colWidthPx,
  onResize,
  useFixedLayout,
}: {
  header: IHeader;
  sorting: ISorting;
  onSortingClick: (headerId: string) => void;
  colWidthPx?: number;
  onResize?: (columnId: string, newWidth: number) => void;
  useFixedLayout?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: header.id });
  const contentRef = useRef<HTMLDivElement>(null);
  const [minContentWidth, setMinContentWidth] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (!contentRef.current || !onResize || minContentWidth !== undefined) {
      return;
    }
    
    const measure = () => {
      const contentRect = contentRef.current?.getBoundingClientRect();
      if (contentRect && contentRect.width > 0) {
        const minWidth = Math.ceil(contentRect.width) + 8;
        setMinContentWidth(minWidth);
      }
    };
    
    requestAnimationFrame(() => {
      requestAnimationFrame(measure);
    });
  }, [onResize, minContentWidth]);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? "none" : transition,
    opacity: isDragging ? 0.7 : 1,
    position: "relative",
    ...(useFixedLayout
      ? {}
      : {
          width:
            (header.width && header.width > 0 ? header.width : colWidthPx)
              ? `${(header.width && header.width > 0 ? header.width : colWidthPx) as number}px`
              : undefined,
          minWidth:
            (header.width && header.width > 0 ? header.width : colWidthPx)
              ? `${(header.width && header.width > 0 ? header.width : colWidthPx) as number}px`
              : undefined,
        }),
  };

  const sortingEnabled = header.enableSorting !== false;
  const isSorted = sorting.field === header.id;

  return (
    <th
      ref={setNodeRef}
      style={style}
      data-col-id={header.id}
      className="sticky top-0 bg-accent-Default-dark text-white z-10 px-1 py-2 font-normal text-lg"
      scope="col"
    >
      <div
        ref={contentRef}
        className={`flex items-center ${useFixedLayout ? "overflow-hidden" : ""} gap-2`}
      >
        <div
          className={`flex items-center flex-shrink-0 whitespace-nowrap overflow-hidden text-ellipsis ${typeof header.header === "string" ? "cursor-move" : ""}`}
          aria-label={typeof header.header === "string" ? "Reorder column" : undefined}
          {...(typeof header.header === "string" ? listeners : {})}
        >
          {typeof header.header === "string" ? <span>{header.header}</span> : header.header}
        </div>

        {sortingEnabled && (
          <button
            type="button"
            className="cursor-pointer flex items-center justify-center p-2"
            aria-label={`Sort by ${typeof header.header === "string" ? header.header : header.id}`}
            onClick={() => onSortingClick(header.id)}
          >
            {isSorted ? (
              sorting.order === "asc" ? (
                <SortAsending className="table-sort-icon" />
              ) : (
                <SortDecending className="table-sort-icon" />
              )
            ) : (
              <span className="table-sort-icon">
                <SortDefault />
              </span>
            )}
          </button>
        )}

        <div className="flex-1" />
      </div>
      {onResize && (
        <ColumnResizeHandle
          onResize={onResize}
          columnId={header.id}
          minWidth={
            minContentWidth ||
            (header.width && header.width > 0
              ? header.width
              : colWidthPx
              ? Math.max(50, colWidthPx * 0.6)
              : undefined)
          }
        />
      )}
    </th>
  );
}

function getHeaderTitle(header: string | React.ReactNode, id: string): string {
  if (typeof header === "string") {
    return header;
  }
  return id;
}

export default function PaginatedTable({
  data,
  totalCount,
  columnsDef,
  sorting,
  pagination,
  endpoint,
  onSortingChange,
  onPaginationChange,
  showColumnVisibility = true,
  showExcelExport = true,
  otherButtons,
  bottomPagination = true,
  topPagination = false,
  excelHeaders,
  formatExcelData,
  formatData,
  emptyStateMessage,
  onExportAll,
  excelFilename: excelFilenameProp,
  tableKey: tableKeyProp,
  forcedColumnVisibility,
  defaultColumnVisibility,
  topContent,
  getRowClassName,
}: ITableProps): React.ReactElement {
  const { pathname } = useLocation();
  const effectiveTableKey = tableKeyProp !== undefined ? tableKeyProp : pathname;
  const { tablePrefs, updateTablePrefs } = useTablePreferences(effectiveTableKey);
  const [openExcelModal, setOpenExcelModal] = useState(false);

  const defaultOrder = useMemo(() => columnsDef.map((h) => h.id), [columnsDef]);
  const [columnOrder, setColumnOrder] = useState<string[]>(defaultOrder);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const hydratedColumnPrefs = useRef(false);

  useEffect(() => {
    setColumnOrder((prev) => {
      const defaultSet = new Set(defaultOrder);
      const prevSet = new Set(prev);
      const columnsChanged =
        defaultSet.size !== prevSet.size ||
        defaultOrder.some((id) => !prevSet.has(id));
      return columnsChanged ? defaultOrder : prev;
    });
    setColumnVisibility((prev) => {
      const next: Record<string, boolean> = { ...prev };
      columnsDef.forEach((h) => {
        next[h.id] =
          forcedColumnVisibility?.[h.id] ??
          tablePrefs?.columnVisibility?.[h.id] ??
          defaultColumnVisibility?.[h.id] ??
          true;
      });
      Object.keys(next).forEach((key) => {
        if (!columnsDef.some((h) => h.id === key)) {
          delete next[key];
        }
      });
      return next;
    });
  }, [columnsDef, defaultOrder, forcedColumnVisibility, defaultColumnVisibility, tablePrefs?.columnVisibility]);

  useEffect(() => {
    if (hydratedColumnPrefs.current || !effectiveTableKey) {
      return;
    }
    const order = tablePrefs?.columnOrder?.filter((id) => columnsDef.some((h) => h.id === id));
    const vis = tablePrefs?.columnVisibility;
    if (order?.length) {
      const rest = defaultOrder.filter((id) => !order.includes(id));
      setColumnOrder([...order, ...rest]);
      hydratedColumnPrefs.current = true;
    }
    if (vis && Object.keys(vis).length) {
      setColumnVisibility((prev) => {
        const next = { ...prev };
        columnsDef.forEach((h) => {
          if (vis[h.id] !== undefined) {
            next[h.id] = vis[h.id];
          }
        });
        return next;
      });
      hydratedColumnPrefs.current = true;
    }
  }, [effectiveTableKey, tablePrefs, columnsDef, defaultOrder]);

  useEffect(() => {
    hydratedColumnPrefs.current = false;
  }, [effectiveTableKey]);

  const formattedTableData = useMemo(() => {
    return formatData ? formatData(data) : data;
  }, [data, formatData]);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!active?.id || !over?.id || active.id === over.id) {
      return;
    }

    setColumnOrder((prevOrder) => {
      const oldIndex = prevOrder.indexOf(active.id as string);
      const newIndex = prevOrder.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) {
        return prevOrder;
      }
      const next = arrayMove([...prevOrder], oldIndex, newIndex);
      if (effectiveTableKey) {
        updateTablePrefs({ columnOrder: next }).catch(() => {});
      }
      return next;
    });
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  const orderedHeaders = useMemo(() => {
    const byId = new Map(columnsDef.map((h) => [h.id, h] as const));
    return columnOrder.map((id) => byId.get(id)).filter(Boolean) as IHeader[];
  }, [columnsDef, columnOrder]);

  const visibleHeaders = useMemo(() => {
    return orderedHeaders.filter((h) => {
      if (forcedColumnVisibility?.[h.id] !== undefined) {
        return forcedColumnVisibility[h.id];
      }
      return columnVisibility[h.id] !== false;
    });
  }, [orderedHeaders, columnVisibility, forcedColumnVisibility]);

  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [measuredWidths, setMeasuredWidths] = useState<Record<string, number>>({});
  const [resizedWidths, setResizedWidths] = useState<Record<string, number>>({});
  const scrollAnimationRef = useRef<number | null>(null);
  const targetScrollRef = useRef<number | null>(null);

  const getColumnWidth = useCallback(
    (header: IHeader): number | undefined => {
      if (header.width && header.width > 0) {
        return header.width;
      }
      if (resizedWidths[header.id]) {
        return resizedWidths[header.id];
      }
      return measuredWidths[header.id];
    },
    [resizedWidths, measuredWidths]
  );

  const hasAllWidths = useMemo(() => {
    return visibleHeaders.every((h) => getColumnWidth(h) !== undefined);
  }, [visibleHeaders, getColumnWidth]);

  const colgroup = useMemo(() => {
    return (
      <colgroup>
        {visibleHeaders.map((h) => {
          const w = getColumnWidth(h);
          return (
            <col
              key={h.id}
              style={w ? { width: `${w}px` } : undefined}
            />
          );
        })}
      </colgroup>
    );
  }, [visibleHeaders, getColumnWidth]);

  const handleColumnResize = useCallback((columnId: string, newWidth: number) => {
    setResizedWidths((prev) => ({ ...prev, [columnId]: newWidth }));
  }, []);

  const visibleHeaderIdsKey = useMemo(
    () => visibleHeaders.map((h) => h.id).join("|"),
    [visibleHeaders]
  );

  useLayoutEffect(() => {
    const headerScroll = headerScrollRef.current;
    if (!headerScroll) {
      return;
    }

    const measure = () => {
      const next: Record<string, number> = {};
      const ths = headerScroll.querySelectorAll<HTMLTableCellElement>("th[data-col-id]");
      ths.forEach((th) => {
        const id = th.dataset.colId;
        if (!id) {
          return;
        }
        next[id] = Math.ceil(th.getBoundingClientRect().width);
      });
      setMeasuredWidths((prev) => ({ ...prev, ...next }));
    };

    measure();

    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) {
      ro.observe(headerScroll);
    }
    window.addEventListener("resize", measure);

    return () => {
      if (ro) {
        ro.disconnect();
      }
      window.removeEventListener("resize", measure);
    };
  }, [visibleHeaderIdsKey, visibleHeaders.length]);

  const handleSortingClick = (headerId: string) => {
    const header = columnsDef.find((h) => h.id === headerId);
    if (!header || header.enableSorting === false) {
      return;
    }
    onSortingChange(nextSort(sorting, headerId));
  };

  const handlePageChange = (pageNumber: number, pageSize: number) => {
    onPaginationChange({
      pageIndex: Math.max(0, pageNumber - 1),
      pageSize,
    });
  };

  const toggleColumnVisibility = (columnId: string) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [columnId]: !(prev[columnId] ?? true) };
      if (effectiveTableKey) {
        updateTablePrefs({ columnVisibility: next }).catch(() => {});
      }
      return next;
    });
  };

  const handleBodyHorizontalScroll = () => {
    const body = bodyScrollRef.current;
    const header = headerScrollRef.current;
    if (!body || !header) {
      return;
    }
    header.scrollLeft = body.scrollLeft;
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") {
        return;
      }

      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable ||
        target.closest("input") ||
        target.closest("textarea")
      ) {
        return;
      }
      
      const body = bodyScrollRef.current;
      const header = headerScrollRef.current;
      const container = tableContainerRef.current;
      if (!body || !header || !container) {
        return;
      }
      
      const containerRect = container.getBoundingClientRect();
      if (containerRect.width === 0 || containerRect.height === 0) {
        return;
      }

      const findScrollContainer = (): HTMLElement | null => {
        if (body.scrollWidth > body.clientWidth) {
          return body;
        }

        if (container) {
          let parent: HTMLElement | null = container.parentElement;
          while (parent) {
            const style = window.getComputedStyle(parent);
            const hasOverflowX = style.overflowX === "auto" || 
                                style.overflowX === "scroll" || 
                                style.overflow === "auto" || 
                                style.overflow === "scroll";
            
            if (hasOverflowX && parent.scrollWidth > parent.clientWidth) {
              return parent;
            }
            
            if (parent.scrollWidth > parent.clientWidth) {
              const currentScroll = parent.scrollLeft;
              parent.scrollLeft = currentScroll + 1;
              const canScroll = parent.scrollLeft !== currentScroll;
              parent.scrollLeft = currentScroll;
              
              if (canScroll) {
                return parent;
              }
            }
            
            parent = parent.parentElement;
          }
        }

        return null;
      };

      const scrollContainer = findScrollContainer();
      if (!scrollContainer) {
        return;
      }

      const scrollAmount = 50;
      const scrollDelta = e.key === "ArrowLeft" ? -scrollAmount : scrollAmount;
      
      e.preventDefault();
      const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      const currentScroll = scrollContainer.scrollLeft;
      
      const newTargetScroll = targetScrollRef.current !== null
        ? Math.max(0, Math.min(maxScroll, targetScrollRef.current + scrollDelta))
        : Math.max(0, Math.min(maxScroll, currentScroll + scrollDelta));
      
      targetScrollRef.current = newTargetScroll;
      
      if (scrollAnimationRef.current !== null) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
      
      const animateScroll = () => {
        const current = scrollContainer.scrollLeft;
        const target = targetScrollRef.current ?? current;
        const distance = target - current;
        
        if (Math.abs(distance) < 1) {
          scrollContainer.scrollLeft = target;
          if (scrollContainer === body) {
            header.scrollLeft = target;
          } else {
            body.scrollLeft = target;
            header.scrollLeft = target;
          }
          scrollAnimationRef.current = null;
          targetScrollRef.current = null;
          return;
        }
        
        const step = distance * 0.15;
        const newScroll = current + step;
        
        scrollContainer.scrollLeft = newScroll;
        
        if (scrollContainer === body) {
          header.scrollLeft = newScroll;
        } else {
          body.scrollLeft = newScroll;
          header.scrollLeft = newScroll;
        }
        
        scrollAnimationRef.current = requestAnimationFrame(animateScroll);
      };
      
      scrollAnimationRef.current = requestAnimationFrame(animateScroll);
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      if (scrollAnimationRef.current !== null) {
        cancelAnimationFrame(scrollAnimationRef.current);
        scrollAnimationRef.current = null;
      }
      targetScrollRef.current = null;
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, []);

  const exportToExcel = async (currentData: boolean) => {
    try {
      const sourceData = currentData
        ? data
        : onExportAll
          ? await onExportAll()
          : null;

      if (!sourceData) {
        return;
      }

      let dataForExcel: any[];
      if (formatExcelData) {
        const result = formatExcelData(sourceData, currentData);
        dataForExcel = result instanceof Promise ? await result : result;
      } else if (formatData) {
        dataForExcel = formatData(sourceData);
      } else {
        dataForExcel = sourceData;
      }

      const titlesToExport = excelHeaders ?? columnsDef.map((h) => getHeaderTitle(h.header, h.id));
      const titleToHeader = new Map(columnsDef.map((h) => [getHeaderTitle(h.header, h.id), h] as const));

      const exportColumns = titlesToExport
        .map((title) => titleToHeader.get(title))
        .filter(Boolean)
        .filter((h) => !FIELDS_TO_IGNORE.has((h as IHeader).id))
        .filter((h) => (currentData ? columnVisibility[(h as IHeader).id] !== false : true)) as IHeader[];

      const rows = dataForExcel.map((row: any, rowIndex: number) => {
        const out: Record<string, any> = {};
        exportColumns.forEach((col) => {
          const resolved = row?.[col.id];
          const colTitle = getHeaderTitle(col.header, col.id);
          out[colTitle] = safeExcelValue(resolved);
        });
        return out;
      });

      const outputFilename = excelFilenameProp ?? buildExcelFilename(endpoint);
      await exportStyledExcel({
        filename: outputFilename,
        title: getExcelTitleFromFilename(outputFilename),
        rows,
        columns: exportColumns.map((col) => ({
          key: getHeaderTitle(col.header, col.id),
          header: getHeaderTitle(col.header, col.id),
        })),
      });
    } catch (e) {
      console.error("Error al exportar a Excel:", e);
    } finally {
      setOpenExcelModal(false);
    }
  };

  return (
    <>
      {showExcelExport && (
        <ExcelModal
          open={openExcelModal}
          setOpen={setOpenExcelModal}
          dataExcelExport={exportToExcel}
        />
      )}

      {(showExcelExport || showColumnVisibility || (otherButtons?.length ?? 0) > 0) && (
        <div className="flex w-full flex-col sm:flex-row justify-end gap-4 py-4">
          {otherButtons?.map((btn) => (
            <div key={btn.text} className="w-full lg:w-fit">
              <LegacyButton
                text={btn.text}
                onClick={btn.onClick}
                className={btn.className || "w-full"}
                color="primary"
                content="text"
              />
            </div>
          ))}

          {showExcelExport && (
            <div className="w-full lg:w-fit">
              <LegacyButton
                text="Export to Excel"
                color="excel"
                content="textIcon"
                onClick={() => setOpenExcelModal(true)}
                icon={
                  <div className="w-5 h-5 ml-2 flex items-center justify-center">
                    <Excel />
                  </div>
                }
                className="w-full"
              />
            </div>
          )}

          {showColumnVisibility && (
            <div className="w-full lg:w-fit">
              <ColumnVisibilityControlSimple
                headers={columnsDef}
                columnVisibility={columnVisibility}
                onToggle={toggleColumnVisibility}
              />
            </div>
          )}
        </div>
      )}

      {topPagination && (
        <PaginationComponent
          totalCount={totalCount}
          currentPage={pagination.pageIndex + 1}
          recordsPerPage={pagination.pageSize}
          onPageChange={handlePageChange}
        />
      )}

      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <div
          ref={tableContainerRef}
          className="table-responsive tabledata w-full"
          tabIndex={0}
        >
          {topContent}
          <div className="sticky top-0 z-20 bg-accent-Default-dark text-white">
            <div
              ref={headerScrollRef}
              className="w-full overflow-hidden"
            >
              <table
                className="table min-w-full w-max"
                style={{ tableLayout: hasAllWidths ? "fixed" : "auto" }}
              >
                {colgroup}
                <thead>
                  <tr className="bg-accent-Default-dark text-white">
                    <SortableContext
                      items={orderedHeaders.map((h) => h.id)}
                      strategy={horizontalListSortingStrategy}
                    >
                      {visibleHeaders.map((h) => (
                        <DraggableHeaderCell
                          key={h.id}
                          header={h}
                          sorting={sorting}
                          onSortingClick={handleSortingClick}
                          colWidthPx={getColumnWidth(h)}
                          onResize={handleColumnResize}
                          useFixedLayout={hasAllWidths}
                        />
                      ))}
                    </SortableContext>
                  </tr>
                </thead>
              </table>
            </div>
          </div>

          <div
            ref={bodyScrollRef}
            className="w-full overflow-x-auto"
            onScroll={handleBodyHorizontalScroll}
          >
            <table
              className="table min-w-full w-max"
              style={{ tableLayout: hasAllWidths ? "fixed" : "auto" }}
            >
              {colgroup}
              <tbody>
                {formattedTableData?.length ? (
                  formattedTableData.map((row, rowIndex) => (
                    <tr
                      key={row?.id ?? rowIndex}
                      className={
                        getRowClassName?.(row, rowIndex) ||
                        "even:bg-gray-100 text-gray-font"
                      }
                    >
                      {visibleHeaders.map((h) => {
                        const value = row?.[h.id];
                        const w = getColumnWidth(h);
                        const allowCellOverflow = h.id === "actions";
                        return (
                          <td
                            key={`${rowIndex}-${h.id}`}
                            className={`px-1 py-2 ${allowCellOverflow ? "overflow-visible" : ""}`}
                            style={
                              hasAllWidths
                                ? {
                                    overflow: allowCellOverflow ? "visible" : "hidden",
                                    maxWidth: w ? `${w}px` : undefined,
                                  }
                                : {
                                    width: w ? `${w}px` : undefined,
                                    minWidth: w ? `${w}px` : undefined,
                                  }
                            }
                          >
                            <div
                              style={
                                hasAllWidths
                                  ? {
                                      overflow: allowCellOverflow ? "visible" : "hidden",
                                      width: "100%",
                                      wordBreak: "break-word",
                                      overflowWrap: "break-word",
                                    }
                                  : undefined
                              }
                            >
                              {h.cell
                                ? h.cell({ row, rowIndex, value })
                                : safeCellValue(value)}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={visibleHeaders.length || 1} className="p-6 text-center">
                      {emptyStateMessage ?? "No data available."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </DndContext>

      {bottomPagination && (
        <PaginationComponent
          totalCount={totalCount}
          currentPage={pagination.pageIndex + 1}
          recordsPerPage={pagination.pageSize}
          onPageChange={handlePageChange}
        />
      )}
    </>
  );
}
